import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { selectCls } from "@/components/Field";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { LABEL_DEFAULTS } from "@/lib/finance";
import { ReportSecPicker, useReportSec } from "@/components/ReportSecPicker";

const LABELS = [["title1", "Title line 1"], ["title2", "Title line 2"], ["balance_forward", "Balance forward"], ["income", "Income heading (I)"],
  ["total_income", "Total income"], ["grand_total_income", "Grand total income"],
  ["expenditure", "Expenditure heading (II)"], ["grand_total_expense", "Grand total expense"], ["balance", "Balance"]];

function LabelsEditor({ labels, set }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {LABELS.map(([k, l]) => (
        <label key={k} className="block text-xs font-bold uppercase tracking-wider text-slate-500">{l}
          <Input value={labels[k] ?? LABEL_DEFAULTS[k]} onChange={(e) => set({ ...labels, [k]: e.target.value })} className={`${inputCls} mt-1 font-normal normal-case tracking-normal`} data-testid={`fin-label-${k}`} />
        </label>
      ))}
    </div>
  );
}

const inputCls = "h-10 bg-white text-slate-900";
const move = (list, i, d) => { const c = [...list]; [c[i], c[i + d]] = [c[i + d], c[i]]; return c; };

function RowTools({ list, i, set, testid }) {
  return (
    <div className="flex shrink-0 gap-1">
      <Button type="button" variant="ghost" size="icon" disabled={i === 0} onClick={() => set(move(list, i, -1))} data-testid={`${testid}-up`}><ArrowUp className="h-4 w-4" /></Button>
      <Button type="button" variant="ghost" size="icon" disabled={i === list.length - 1} onClick={() => set(move(list, i, 1))} data-testid={`${testid}-down`}><ArrowDown className="h-4 w-4" /></Button>
      <Button type="button" variant="ghost" size="icon" onClick={() => set(list.filter((_, j) => j !== i))} data-testid={`${testid}-delete`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
    </div>
  );
}

function IncomeGroups({ groups, set }) {
  return (
    <div className="mb-5 space-y-2" data-testid="income-groups">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Income groups (numbered 1, 2, 3... on the report)</div>
      {groups.map((g, i) => (
        <div key={g.id || `new-${i}`} className="flex items-center gap-2">
          <span className="w-6 text-sm font-bold text-slate-600">{i + 1}.</span>
          <Input value={g.name} onChange={(e) => set(groups.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Group name" className={inputCls} data-testid={`income-group-${i}-name`} />
          <RowTools list={groups} i={i} set={(l) => (l.length ? set(l) : null)} testid={`income-group-${i}`} />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => set([...groups, { name: "" }])} data-testid="income-group-add"><Plus className="mr-2 h-4 w-4" /> Add income group</Button>
    </div>
  );
}

function IncomeLines({ lines, groups, set }) {
  const { options } = useApp();
  const refs = { tithe_destination: options.destinations, tithe_type: options.giver_types || [], donation_category: options.categories };
  const upd = (i, patch) => set(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div className="space-y-3">
      {lines.map((l, i) => (
        <div key={l.id || `new-${i}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-3" data-testid={`income-line-${i}`}>
          <Input value={l.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Line name" className={`${inputCls} min-w-48 flex-1`} data-testid={`income-line-${i}-label`} />
          <select value={l.group_id || groups[0]?.id || ""} onChange={(e) => upd(i, { group_id: e.target.value })} className={`${selectCls} h-10 w-auto`} data-testid={`income-line-${i}-group`}>
            {groups.map((g, gi) => <option key={g.id || `new-${gi}`} value={g.id || ""} disabled={!g.id}>{gi + 1}. {g.name || "(new group - save first)"}</option>)}
          </select>
          <select value={l.source} onChange={(e) => upd(i, { source: e.target.value, ref: "" })} className={`${selectCls} h-10 w-auto`} data-testid={`income-line-${i}-source`}>
            <option value="tithe_destination">From tithes sent to</option>
            <option value="tithe_type">From tithes of type</option>
            <option value="donation_category">From Conference Collection category</option>
            <option value="manual">Typed in each year</option>
          </select>
          {l.source !== "manual" && (
            <select value={l.ref} onChange={(e) => upd(i, { ref: e.target.value })} className={`${selectCls} h-10 w-auto`} data-testid={`income-line-${i}-ref`}>
              <option value="">Choose...</option>
              {refs[l.source].map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
            </select>
          )}
          <RowTools list={lines} i={i} set={set} testid={`income-line-${i}`} />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => set([...lines, { label: "", source: "manual", ref: "" }])} data-testid="income-line-add"><Plus className="mr-2 h-4 w-4" /> Add income line</Button>
    </div>
  );
}

function ExpenseSection({ s, idx, count, set, onDelete, onMove }) {
  const items = s.items;
  const setItems = (list) => set({ ...s, items: list });
  const tid = s.id || `new-${idx}`;
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-4" data-testid={`exp-section-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Section {idx + 1} name</label>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="icon" disabled={idx === 0} onClick={() => onMove(-1)} data-testid={`exp-section-${idx}-up`}><ArrowUp className="h-4 w-4" /></Button>
          <Button type="button" variant="ghost" size="icon" disabled={idx === count - 1} onClick={() => onMove(1)} data-testid={`exp-section-${idx}-down`}><ArrowDown className="h-4 w-4" /></Button>
          <Button type="button" variant="ghost" size="icon" disabled={count === 1} onClick={onDelete} data-testid={`exp-section-${idx}-delete`} aria-label="Delete section"><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>
      </div>
      <Input value={s.name} onChange={(e) => set({ ...s, name: e.target.value })} className={`${inputCls} font-semibold`} data-testid={`exp-section-${idx}-name`} />
      {items.map((it, i) => (
        <div key={it.id || `new-${i}`} className="flex items-center gap-2">
          <Input value={it.name} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Line item" className={inputCls} data-testid={`exp-section-${idx}-item-${i}`} />
          <RowTools list={items} i={i} set={setItems} testid={`exp-section-${idx}-item-${i}`} />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => setItems([...items, { name: "" }])} data-testid={`exp-section-${idx}-add`}><Plus className="mr-2 h-4 w-4" /> Add line item</Button>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">Subtotal label
        <Input value={s.subtotal_label ?? "Total Expenses"} onChange={(e) => set({ ...s, subtotal_label: e.target.value })} className={`${inputCls} mt-1 font-normal normal-case tracking-normal`} data-testid={`exp-section-${idx}-subtotal-label`} />
      </label>
      <span className="hidden" data-testid={`exp-section-id-${tid}`} />
    </div>
  );
}

export default function AdminFinance() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rsec] = useReportSec();
  useEffect(() => { setCfg(null); api.get("/finance/config", { params: { sec: rsec } }).then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e))); }, [rsec]);
  if (!cfg) return <ReportSecPicker testid="finance-sec-picker" />;

  const save = async () => {
    if (cfg.income_lines.some((l) => !l.label.trim() || (l.source !== "manual" && !l.ref))) { toast.error("Each income line needs a name and a source"); return; }
    if ((cfg.income_groups || []).some((g) => !g.name.trim())) { toast.error("Income group names cannot be empty"); return; }
    if (cfg.expense_sections.some((s) => !s.name.trim() || s.items.some((i) => !i.name.trim()))) { toast.error("Section names and line items cannot be empty"); return; }
    setBusy(true);
    try { const r = await api.put("/finance/config", cfg, { params: { sec: rsec } }); setCfg(r.data); toast.success(`Financial report layout saved for ${rsec === "gc" ? "General Council" : "MWJK"}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const secs = cfg.expense_sections;
  const setSecs = (list) => setCfg({ ...cfg, expense_sections: list });

  return (
    <div className="space-y-6" data-testid="admin-finance">
      <ReportSecPicker testid="finance-sec-picker" />
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg font-bold">Report titles & labels</h3>
        <p className="mb-4 text-sm text-slate-500">Wording used on the General Council Financial Report.</p>
        <LabelsEditor labels={cfg.labels || {}} set={(l) => setCfg({ ...cfg, labels: l })} />
      </section>
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg font-bold">INCOME lines</h3>
        <p className="mb-4 text-sm text-slate-500">Each line totals automatically from tithes or conference collections, or is typed in each year on the Annual Summary.</p>
        <IncomeGroups groups={cfg.income_groups || []} set={(g) => setCfg({ ...cfg, income_groups: g })} />
        <IncomeLines lines={cfg.income_lines} groups={cfg.income_groups || []} set={(l) => setCfg({ ...cfg, income_lines: l })} />
      </section>
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-lg font-bold">EXPENDITURES sections</h3>
        <p className="mb-4 text-sm text-slate-500">Add, rename, reorder or delete sections and their line items, then press Save. Record expenses on the Expenses page.</p>
        <div className="grid gap-4 lg:grid-cols-2">
          {secs.map((s, i) => (
            <ExpenseSection key={s.id || `new-${i}`} s={s} idx={i} count={secs.length}
              set={(v) => setSecs(secs.map((x, j) => (j === i ? v : x)))}
              onMove={(d) => setSecs(move(secs, i, d))}
              onDelete={() => { if (!s.items.length || window.confirm(`Delete the section "${s.name}" and its line items? (Saved when you press Save changes)`)) { setSecs(secs.filter((_, j) => j !== i)); } }} />
          ))}
        </div>
        <Button type="button" variant="outline" className="mt-4" disabled={secs.length >= 20} onClick={() => setSecs([...secs, { name: `Section ${secs.length + 1}`, items: [], subtotal_label: "Total Expenses" }])} data-testid="exp-section-add"><Plus className="mr-2 h-4 w-4" /> Add section</Button>
        {secs.length >= 20 && <span className="ml-3 text-sm text-slate-500" data-testid="exp-section-max">Up to 20 sections</span>}
      </section>
      <div className="flex justify-end"><Button onClick={save} disabled={busy} data-testid="finance-save">Save changes</Button></div>
    </div>
  );
}
